export class ApiError extends Error {
  status: number;
  payload: unknown;

  constructor(status: number, message: string, payload: unknown) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

export type AuthStatus = {
  actor: string;
  authenticated: boolean;
  auth_required: boolean;
  admin_token_configured: boolean;
  open_key_configured: boolean;
  access_authenticated: boolean;
  access_email: string;
  mode: "admin-token" | "open-key" | "open" | "locked";
};

const ADMIN_TOKEN_KEY = "discord_mj_console_admin_token";

export function readAdminToken(): string {
  try {
    return window.sessionStorage.getItem(ADMIN_TOKEN_KEY) || window.localStorage.getItem(ADMIN_TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

export function isAdminTokenRemembered(): boolean {
  try {
    return Boolean(window.localStorage.getItem(ADMIN_TOKEN_KEY));
  } catch {
    return false;
  }
}

export function saveAdminToken(token: string, remember = false) {
  const value = token.trim();
  if (remember) {
    try {
      window.localStorage.setItem(ADMIN_TOKEN_KEY, value);
      window.sessionStorage.removeItem(ADMIN_TOKEN_KEY);
      return;
    } catch {
      // Fall back to the current browser session when persistent storage is unavailable.
    }
  }
  try {
    window.sessionStorage.setItem(ADMIN_TOKEN_KEY, value);
    window.localStorage.removeItem(ADMIN_TOKEN_KEY);
  } catch {
    // Storage can be unavailable in restricted browser contexts.
  }
}

export function clearAdminToken() {
  try {
    window.sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    window.localStorage.removeItem(ADMIN_TOKEN_KEY);
  } catch {
    // Ignore storage failures; the next request will simply omit the token.
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  const token = readAdminToken();
  if (token && !headers.has("authorization")) {
    headers.set("authorization", `Bearer ${token}`);
  }
  const response = await fetch(path, { ...init, headers });
  const contentType = response.headers.get("content-type") || "";
  const payload = contentType.includes("application/json") ? await response.json() : await response.text();
  if (!response.ok) {
    const message = typeof payload === "object" && payload && "detail" in payload ? String((payload as { detail: unknown }).detail) : response.statusText;
    throw new ApiError(response.status, message, payload);
  }
  return payload as T;
}

export function getAuthStatus() {
  return api<AuthStatus>("/api/auth/status");
}

export function toQuery(params: object): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    query.set(key, String(value));
  }
  const text = query.toString();
  return text ? `?${text}` : "";
}
